// Web: zero-server "viseme puppet". Plays a green-screen idle loop, keys it onto the hall, and
// pastes a feathered mouth crop over the face bbox chosen from the live audio (no ML, heuristic).
// Missing mouth assets => the loop still plays, just without a mouth overlay.
import React, { useEffect, useRef } from 'react';
import { Asset } from 'expo-asset';
import { chromaKey } from './chromaKey';
import { LIVE_ASSETS } from './liveAssets';
import { feathered, loadImage, makeVisemeEngine, visemeOrFallback } from './visemeEngine';
export { pickViseme } from './visemeEngine';
export type { VisemeFeatures } from './visemeEngine';
import type { AvatarPuppetProps } from './AvatarPuppet';

const FPS = 25;
const SRC_W = 720; // mouth_track coordinate space (overridden by the json's width/height)
const SRC_H = 900;
const CW = 540; // canvas size for the green loop (keyed per frame in JS)
const CH = 675;
const ALPHA_MAX_W = 1080; // pre-keyed alpha loops draw at screen resolution up to the source width
// WebKit (Safari, every iOS browser) only keys HEVC alpha; everything else gets VP9 alpha.
const useMov = typeof navigator !== 'undefined' && (/^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent) || /iP(hone|ad|od)/.test(navigator.userAgent));

interface Box { x1: number; y1: number; x2: number; y2: number }
interface Track { width?: number; height?: number; frames: Array<Box & { frame: number }> }

const uriOf = (m: number) => Asset.fromModule(m).uri;


export function AvatarPuppet({ audioTrack, speaking, width, height, left, top, deity = 'clio', onFirstFrame }: AvatarPuppetProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const firstRef = useRef(onFirstFrame);
  firstRef.current = onFirstFrame;
  const speakingRef = useRef(speaking);
  speakingRef.current = speaking;
  const trackRef = useRef(audioTrack);
  trackRef.current = audioTrack;
  const assets = LIVE_ASSETS[deity];

  // Audio tap: analyser on the remote track. Playback is untouched (separate <audio> element).
  const analyserRef = useRef<{ an: AnalyserNode; ctx: AudioContext } | null>(null);
  useEffect(() => {
    if (!audioTrack) return;
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const src = ctx.createMediaStreamSource(new MediaStream([audioTrack]));
    const an = ctx.createAnalyser();
    an.fftSize = 1024;
    an.smoothingTimeConstant = 0.3;
    const zero = ctx.createGain();
    zero.gain.value = 0;
    src.connect(an);
    an.connect(zero);
    zero.connect(ctx.destination);
    void ctx.resume().catch(() => {});
    analyserRef.current = { an, ctx };
    return () => {
      analyserRef.current = null;
      try { src.disconnect(); an.disconnect(); zero.disconnect(); } catch { /* already gone */ }
      void ctx.close().catch(() => {});
    };
  }, [audioTrack]);

  useEffect(() => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || !assets) return;
    const alpha = !!(assets.alphaWebm && assets.alphaMov); // pre-keyed: no per-frame chroma key
    const cw = c.width;
    const ch = c.height;
    const ctx = c.getContext('2d', alpha ? undefined : { willReadFrequently: true });
    if (!ctx) return;
    let stopped = false;
    let raf = 0;
    let announced = false;

    v.src = uriOf(alpha ? (useMov ? assets.alphaMov! : assets.alphaWebm!) : assets.loop);
    const tryPlay = () => void v.play().catch(() => {});
    tryPlay();
    const onGesture = () => { tryPlay(); };
    window.addEventListener('pointerdown', onGesture, { once: true });
    window.addEventListener('keydown', onGesture, { once: true });

    // Mouth assets (optional)
    let track: Track | null = null;
    const crops: Record<string, HTMLCanvasElement> = {};
    if (assets.mouthTrack && assets.crops) {
      track = assets.mouthTrack as Track;
      const names = Object.keys(assets.crops);
      void Promise.all(
        names.map(async (n) => {
          try { crops[n] = feathered(await loadImage(uriOf(assets.crops![n]))); } catch { /* skip this shape */ }
        }),
      );
    }
    const sx = cw / (track?.width ?? SRC_W);
    const sy = ch / (track?.height ?? SRC_H);

    const engine = makeVisemeEngine(() => analyserRef.current, () => speakingRef.current);

    const draw = (now: number) => {
      if (stopped) return;
      raf = requestAnimationFrame(draw);
      if (v.readyState < 2 || v.videoWidth === 0) return;

      const { cur, prev, fadeT: t } = engine.step(now);

      if (alpha) ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(v, 0, 0, cw, ch);
      const shape = visemeOrFallback(cur, crops);
      const prevShape = visemeOrFallback(prev, crops);
      if (track && shape) {
        const idx = Math.floor(v.currentTime * FPS) % track.frames.length;
        const b = track.frames[idx] ?? track.frames[track.frames.length - 1];
        if (b) {
          const x = b.x1 * sx, y = b.y1 * sy, w = (b.x2 - b.x1) * sx, h = (b.y2 - b.y1) * sy;
          if (t < 1 && prevShape) {
            ctx.globalAlpha = 1;
            ctx.drawImage(prevShape, x, y, w, h);
          }
          ctx.globalAlpha = t < 1 && prevShape ? t : 1;
          ctx.drawImage(shape, x, y, w, h);
          ctx.globalAlpha = 1;
        }
      }
      if (!alpha) {
        const img = ctx.getImageData(0, 0, cw, ch);
        chromaKey(img.data);
        ctx.putImageData(img, 0, 0);
      }
      if (!announced) {
        announced = true;
        firstRef.current?.();
      }
    };
    raf = requestAnimationFrame(draw);
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
      v.pause();
      v.removeAttribute('src');
      v.load();
    };
  }, [assets]);

  if (!assets) return null;
  const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 3) : 1;
  const hasAlpha = !!(assets.alphaWebm && assets.alphaMov);
  const canvasW = hasAlpha ? Math.min(ALPHA_MAX_W, Math.round(width * dpr)) : CW;
  const canvasH = hasAlpha ? Math.round((canvasW * height) / width) : CH;
  return React.createElement(
    React.Fragment,
    null,
    React.createElement('video', {
      ref: videoRef,
      autoPlay: true,
      loop: true,
      muted: true,
      playsInline: true,
      style: { position: 'absolute', left: 0, top: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none' },
    }),
    React.createElement('canvas', {
      ref: canvasRef,
      width: canvasW,
      height: canvasH,
      style: { position: 'absolute', left, top, width, height, pointerEvents: 'none' },
    }),
  );
}
